import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import { Meta } from "admin/types/meta";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import { User } from "admin/types/user";
import { RootState } from "admin/store";
import {
  API_BASE_PATH,
  COMPANY_ADMIN_API_VER,
} from "admin/constants/constants";
import { AdminTypes } from "admin/constants/adminTypes";
import { PageContext } from "admin/types/pageContext";
import { objectToFormData } from "admin/utils/objectUtil";

const key = "user";
const adapter = createEntityAdapter<User.User>();

const initialStateValues: User.UserState = {
  user: null,
  errors: null,
  loading: false,
  meta: null,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async **/
export const fetchUsers = createAsyncThunk<
  {
    users: User.User[];
    meta: Meta;
  },
  {
    context: PageContext.PageContext;
    param?: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchUsers`, async (args, { rejectWithValue }) => {
  try {
    const { context, param, isServerSide = false, headers, locale } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/users.json?${param}`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/users.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      users: result.data.users,
      meta: result.data.meta,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postUser = createAsyncThunk<
  {
    user: User.User;
  },
  {
    context: PageContext.PageContext;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postUser`, async (args, { rejectWithValue }) => {
  try {
    const { context, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/users.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/users.json`;
    const result = await createWrapper().post(url, {
      user: params,
    });
    return {
      user: result.data.user,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchUser = createAsyncThunk<
  {
    user: User.User;
  },
  {
    context: PageContext.PageContext;
    id: number;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchUser`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, isServerSide = false, headers, locale } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/users/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/users/${id}.json`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      user: result.data.user,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putUser = createAsyncThunk<
  {
    user: User.User;
  },
  { context: PageContext.PageContext; id: number; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putUser`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/users/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/users/${id}.json`;
    const result = await createWrapper().put(url, { user: params });
    return {
      user: result.data.user,
    };
  } catch (err) {
    console.log(err.response);
    return rejectWithValue(err.data.errors);
  }
});

export const deleteUser = createAsyncThunk<
  { id: number },
  { context: PageContext.PageContext; id: number },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/deleteUser`, async (args, { rejectWithValue }) => {
  try {
    const { context, id } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/users/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/users/${id}.json`;
    await createWrapper().delete(url);
    return { id: id };
  } catch (err) {
    console.log(err.response);
    return rejectWithValue(err.data.errors);
  }
});

export const postProfileCsvImport = createAsyncThunk<
  void,
  {
    context: PageContext.PageContext;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postProfileCsvImport`, async (args, { rejectWithValue }) => {
  try {
    const { context, params } = args;
    console.log(params);
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/profile_csv_import.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/profile_csv_import.json`;
    const formData: any = new FormData();
    objectToFormData(params, formData);
    await createWrapper({ isMultipart: true }).post(url, formData);
    return;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice **/
export const userSlice = createSlice({
  name: key,
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
    },
    setUsers: (state, action) => {
      adapter.setAll(state, action.payload.users);
      state.meta = action.payload.meta;
    },
    clearUser: (state) => {
      state.user = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        return { ...state, ...action.payload.user };
      })
      .addCase(postUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.users);
        state.meta = action.payload.meta;
      })
      .addCase(fetchUser.pending, (state, action) => {
        const targetUserId = action.meta.arg.id;
        if (state.user && state.user.id !== targetUserId) {
          // 別の会員データを取得するため選択中の会員をクリアする
          state.user = null;
        }
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
      })
      .addCase(putUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        adapter.removeOne(state, action.payload.id);
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector **/
export const {
  selectById: userByIdSelector,
  selectIds: userIdsSelector,
  selectEntities: userEntitiesSelector,
  selectAll: usersSelector,
  selectTotal: totalUsersSelector,
} = adapter.getSelectors((state: RootState) => state.user);

const stateSelector = (state: RootState) => state[key];

export const userSelector = createSelector(
  stateSelector,
  (state) => state.user,
);

export const errorsSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const usersMetaSelector = createSelector(
  stateSelector,
  (state) => state.meta,
);

/** action export **/
export const { setUser, setUsers, clearUser } = userSlice.actions;

export const convertUserToFormValues = (data: any) => {
  const { profile, emailPermissions } = data;
  return { profile, emailPermissions };
};

export const convertUserImportFormValuesToParams = (data: any) => {
  const params = { ...data };
  if (params.file) {
    params.file = params.file[0];
  }
  return params;
};
